// The tiny expression language behind the customer workbook.
//
// Each lever's arithmetic is written ONCE as an `Expr` tree (engine/xlsx-lever-specs.ts) and rendered
// two ways: `toExcel()` produces the live formula the customer edits, `evalExpr()` produces the
// number the engine would — and the parity test pins the second to the golden fixtures. That is
// what keeps the workbook from drifting the way the hand-transcribed Python builder did.
//
// Pure module: no imports, no knowledge of levers or sheets.

export type Expr =
  | { t: 'lit'; v: number }
  /** A cell or global by key — an input, an earlier calc, a profile field or a constant. */
  | { t: 'ref'; k: string }
  /**
   * A percentage input. The workbook stores percent cells as Excel-native fractions (25% is 0.25)
   * so the customer types "25%" and formulas use the cell directly; the engine stores 25 and
   * divides by 100. `pct` renders to the bare cell in Excel and to `get(k) / 100` in JS.
   */
  | { t: 'pct'; k: string }
  | { t: 'add'; a: Expr[] }
  | { t: 'mul'; a: Expr[] }
  | { t: 'sub'; a: Expr; b: Expr }
  | { t: 'div'; a: Expr; b: Expr }
  | { t: 'max'; a: Expr; b: Expr }
  /** Σ over a contiguous run of cells — SUM(first:last) in Excel. */
  | { t: 'sum'; keys: string[] }
  /** applyTimeMode(): zero when "time as hours" is on. Renders to IF(<toggle>="Yes",0,a). */
  | { t: 'tat0'; a: Expr };

export const lit = (v: number): Expr => ({ t: 'lit', v });
export const ref = (k: string): Expr => ({ t: 'ref', k });
export const pct = (k: string): Expr => ({ t: 'pct', k });
export const add = (...a: Expr[]): Expr => ({ t: 'add', a });
export const mul = (...a: Expr[]): Expr => ({ t: 'mul', a });
export const sub = (a: Expr, b: Expr): Expr => ({ t: 'sub', a, b });
export const div = (a: Expr, b: Expr): Expr => ({ t: 'div', a, b });
export const max = (a: Expr, b: Expr): Expr => ({ t: 'max', a, b });
export const max0 = (a: Expr): Expr => max(a, lit(0));
export const sum = (keys: string[]): Expr => ({ t: 'sum', keys });
export const tat0 = (a: Expr): Expr => ({ t: 'tat0', a });

export interface ExcelEnv {
  /** Absolute reference for a key: '$C$6', 'Summary!$B$5', 'Constants!$B$3'. */
  cell(key: string): string;
  /** The cell holding the Yes/No "time as hours" toggle. */
  timeToggleCell: string;
}

export interface EvalEnv {
  get(key: string): number;
  timeAsTime: boolean;
}

function isAtom(e: Expr): boolean {
  return e.t === 'lit' || e.t === 'ref' || e.t === 'pct' || e.t === 'max' || e.t === 'sum' || e.t === 'tat0';
}

/** 'Inputs!$B$9' + 'Inputs!$B$12' → 'Inputs!$B$9:$B$12' (a range names its sheet once). */
function rangeRef(first: string, last: string): string {
  const bang = first.lastIndexOf('!');
  if (bang >= 0 && last.startsWith(first.slice(0, bang + 1))) return `${first}:${last.slice(bang + 1)}`;
  return `${first}:${last}`;
}

/** Render to an Excel formula body (no leading '='). Parenthesises only where precedence demands. */
export function toExcel(e: Expr, env: ExcelEnv): string {
  const factor = (x: Expr) => (isAtom(x) || x.t === 'mul' || x.t === 'div' ? toExcel(x, env) : `(${toExcel(x, env)})`);
  switch (e.t) {
    case 'lit':
      return String(e.v);
    case 'ref':
    case 'pct':
      return env.cell(e.k);
    case 'add':
      return e.a.map((x) => toExcel(x, env)).join('+');
    case 'mul':
      return e.a.map(factor).join('*');
    case 'sub':
      return `${toExcel(e.a, env)}-${isAtom(e.b) ? toExcel(e.b, env) : `(${toExcel(e.b, env)})`}`;
    case 'div':
      return `${factor(e.a)}/${isAtom(e.b) ? toExcel(e.b, env) : `(${toExcel(e.b, env)})`}`;
    case 'max':
      return `MAX(${toExcel(e.a, env)},${toExcel(e.b, env)})`;
    case 'sum':
      return e.keys.length ? `SUM(${rangeRef(env.cell(e.keys[0]), env.cell(e.keys[e.keys.length - 1]))})` : '0';
    case 'tat0':
      return `IF(${env.timeToggleCell}="Yes",0,${toExcel(e.a, env)})`;
  }
}

/** Evaluate with the engine's semantics (percent inputs are whole numbers; time mode zeroes). */
export function evalExpr(e: Expr, env: EvalEnv): number {
  switch (e.t) {
    case 'lit':
      return e.v;
    case 'ref':
      return env.get(e.k);
    case 'pct':
      return env.get(e.k) / 100;
    case 'add':
      return e.a.reduce((s, x) => s + evalExpr(x, env), 0);
    case 'mul':
      return e.a.reduce((p, x) => p * evalExpr(x, env), 1);
    case 'sub':
      return evalExpr(e.a, env) - evalExpr(e.b, env);
    case 'div':
      return evalExpr(e.a, env) / evalExpr(e.b, env);
    case 'max':
      return Math.max(evalExpr(e.a, env), evalExpr(e.b, env));
    case 'sum':
      return e.keys.reduce((s, k) => s + env.get(k), 0);
    case 'tat0':
      return env.timeAsTime ? 0 : evalExpr(e.a, env);
  }
}

/** Every key the expression reads, split by how it reads them (the drift test checks both). */
export function collectRefs(e: Expr, out = { refs: new Set<string>(), pcts: new Set<string>() }): { refs: Set<string>; pcts: Set<string> } {
  switch (e.t) {
    case 'lit':
      break;
    case 'ref':
      out.refs.add(e.k);
      break;
    case 'pct':
      out.pcts.add(e.k);
      break;
    case 'add':
    case 'mul':
      e.a.forEach((x) => collectRefs(x, out));
      break;
    case 'sub':
    case 'div':
    case 'max':
      collectRefs(e.a, out);
      collectRefs(e.b, out);
      break;
    case 'sum':
      e.keys.forEach((k) => out.refs.add(k));
      break;
    case 'tat0':
      collectRefs(e.a, out);
      break;
  }
  return out;
}

/** True when the expression contains a time-mode gate anywhere. */
export function usesTimeMode(e: Expr): boolean {
  switch (e.t) {
    case 'tat0':
      return true;
    case 'add':
    case 'mul':
      return e.a.some(usesTimeMode);
    case 'sub':
    case 'div':
    case 'max':
      return usesTimeMode(e.a) || usesTimeMode(e.b);
    default:
      return false;
  }
}
